// The sales invoice's 'Cash rounding' field (ops-simulation T07): pre-filled
// OFF (cash rounding is for cash; the payment mode is not known yet) — switching
// it on starts from the Settings rule for the order's location, editable.
// The API applies the rule to the invoice total and keeps the adjustment apart.

import { Toggle } from '@bbpos/ui';
import { resolveCashRoundingRule, type CashRoundingRule } from '@bbpos/utils';
import { useAuthStore } from '@/stores/use-auth-store';
import { CashRoundingFields } from '@/components/features/settings/CashRoundingFields';

type StoredRule = { cash_rounding_step?: number | null; cash_rounding_mode?: string | null };

/** The Settings rule for a location: its override, else the tenant default. */
export function useDefaultCashRounding(currency: string, locationId: string | undefined): CashRoundingRule {
  const tenant = useAuthStore((s) => s.profile?.tenant) as StoredRule | undefined;
  const locations = useAuthStore((s) => s.profile?.locations) as Array<{ id: string } & StoredRule> | undefined;
  const location = locations?.find((l) => l.id === locationId);
  return resolveCashRoundingRule(
    currency,
    { step: tenant?.cash_rounding_step, mode: tenant?.cash_rounding_mode },
    { step: location?.cash_rounding_step, mode: location?.cash_rounding_mode },
  );
}

interface InvoiceCashRoundingProps {
  currency: string;
  /** null = no rounding on this invoice. */
  value: CashRoundingRule | null;
  defaultRule: CashRoundingRule;
  onChange: (rule: CashRoundingRule | null) => void;
}

export function InvoiceCashRounding({ currency, value, defaultRule, onChange }: InvoiceCashRoundingProps) {
  return (
    <div className="rounded-2xl bg-white/80 dark:bg-neutral-900/80 border border-teal-100 dark:border-teal-900/50 shadow-md p-5 mb-6 animate-slide-up space-y-3">
      <Toggle
        id="invoice-cash-rounding"
        label="Cash rounding"
        description="Round the invoice total for a cash payment. Off bills the exact amount; on uses your Settings rule."
        checked={value !== null}
        onChange={(on) => onChange(on ? defaultRule : null)}
      />
      {value !== null && <CashRoundingFields currency={currency} value={value} onChange={onChange} />}
    </div>
  );
}
